/**
 * Bonsai Mail - Outlook Task Pane (React Rebuild)
 * 
 * Main container component for the Outlook add-in. Handles:
 * - Office.js initialization and environment detection
 * - Authentication (dialog-based for Outlook, redirect for browser)
 * - Email loading from Office.context.mailbox
 * - Tab navigation (File, Bonsai Mate)
 * - User preferences (auto-delete, time recording)
 * - Settings overlay, logout
 *
 * Sub-components:
 *   - FileTab: All File tab features (AI suggestions, favorites, filing, etc.)
 *   - TimePanel: LawBillity time recording overlay
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { errorMessage } from "../../utils/errorMessage";
import { useBonsai } from "../../contexts/BonsaiContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import {
  Search, FolderOpen, Mail, Paperclip, CheckCircle2, ExternalLink,
  Loader2, LogIn, RefreshCw, AlertTriangle, Bug, Sparkles,
  Send, Clock, FileText, Bot, User, Zap, Reply, Trash2,
  Settings, LogOut, ChevronDown, HelpCircle, Globe, Inbox,
  FileCheck, ClipboardList,
} from "lucide-react";
import { fmtUK } from "../../lib/dateUtils";
import { parseMatterRef } from "../../lib/matterRefUtils";
import axios from "axios";
import { API } from '../../config/api';
import FileTab from './outlook/FileTab';
import TimePanel from './outlook/TimePanel';
import BrowsePanel from './outlook/BrowsePanel';
import HelpPanel from './outlook/HelpPanel';
import AttachmentSavePrompt from '../../components/AttachmentSavePrompt';
import AdditionalDestinationsPicker from '../../components/AdditionalDestinationsPicker';
import { summariseAdditionalCopies } from '../../components/AdditionalCopiesConfirmation';

const MATE_API = 'https://bonsaimail.azurewebsites.net/api/bonsai/mail';

// FIX(#182 Diff 4): DEBUG_MODE driven by build env — never true in production.
const DEBUG_MODE = import.meta.env.DEV;

// Standing-rule helper: every FE error must surface via window.errorMessage
// when available, with a sane local fallback. Mirrors the BrowsePanel.jsx
// pattern (lines 18-31) so a future eyeball-pass sees the same shape.
function _formatError(err, fallback) {
  if (typeof window !== 'undefined' && typeof window.errorMessage === 'function') {
    try {
      return window.errorMessage(err, fallback);
    } catch (_) { /* fall through */ }
  }
  if (typeof err === 'string') return err || fallback;
  return errorMessage(err, fallback);
}

// Detect the backend's "destination folder not found" signal (see
// backend/bonsai/bonsai_filing.py:1933) so the FE can offer a Refresh
// action instead of just dropping a raw error string on the user.
function _isFolderNotFound(err) {
  const detail = (err?.response?.data?.detail || err?.response?.data?.error || err?.message || '') + '';
  const status = err?.response?.status;
  return (
    status === 404 ||
    /destination folder (was )?not found/i.test(detail) ||
    /folder.*not.*found/i.test(detail) ||
    /itemNotFound/i.test(detail)
  );
}

// Robust Office environment detection
const detectOfficeEnvironment = () => {
  const result = {
    hasOffice: false, hasContext: false, hasMailbox: false, hasUI: false,
    host: null, platform: null, isOutlook: false, canUseDialog: false, details: []
  };
  try {
    if (typeof Office !== 'undefined' && Office !== null) {
      result.hasOffice = true;
      result.details.push("Office object exists");
      if (Office.context) {
        result.hasContext = true;
        result.details.push("Office.context exists");
        if (Office.context.host) {
          result.host = Office.context.host;
          result.details.push(`Host: ${Office.context.host}`);
        }
        if (Office.HostType && Office.context.host === Office.HostType.Outlook) {
          result.isOutlook = true;
          result.details.push("Running in Outlook (HostType match)");
        } else if (Office.context.host === "Outlook") {
          result.isOutlook = true;
          result.details.push("Running in Outlook (string match)");
        }
        if (Office.context.platform) {
          result.platform = Office.context.platform;
          result.details.push(`Platform: ${Office.context.platform}`);
        }
        if (Office.context.mailbox) {
          result.hasMailbox = true;
          result.details.push("Office.context.mailbox exists");
        }
        if (Office.context.ui) {
          result.hasUI = true;
          result.canUseDialog = typeof Office.context.ui.displayDialogAsync === 'function';
          result.details.push(`UI exists, dialog available: ${result.canUseDialog}`);
        }
      }
    } else {
      result.details.push("Office object not found - running in browser");
    }
  } catch (e) {
    result.details.push(`Detection error: ${e.message}`);
  }
  return result;
};


const SEV = {
  high:   { bg: "bg-status-red-dim/40",   border: "border-red-800/50",   icon: AlertTriangle, iconColor: "text-status-red",   badge: "bg-status-red-dim text-status-red" },
  medium: { bg: "bg-status-amber-dim/30", border: "border-amber-800/40", icon: Clock,          iconColor: "text-status-amber", badge: "bg-status-amber-dim text-status-amber" },
  low:    { bg: "bg-teal-dim/30",  border: "border-teal/40",  icon: CheckCircle2,   iconColor: "text-teal",  badge: "bg-teal-dim text-teal" },
};

function MateSuggestionCard({ s }) {
  const st = SEV[s.severity] || SEV.medium;
  const Icon = st.icon;
  return (
    <div className={`${st.bg} border ${st.border} rounded px-2.5 py-2 flex items-start gap-2`} data-testid={`mate-sug-${s.type}`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${st.iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-white leading-tight">{s.title}</p>
        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{s.detail}</p>
      </div>
      <span className={`text-[9px] px-1 py-0.5 rounded ${st.badge} shrink-0`}>{s.severity}</span>
    </div>
  );
}

function MateChatBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-1.5 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && <Bot className="w-3.5 h-3.5 text-teal mt-1 shrink-0" />}
      <div className={`max-w-[85%] rounded-md px-2.5 py-1.5 text-[11px] leading-relaxed ${
        isUser ? "bg-teal/15 text-white border border-teal/20" : "bg-navy text-slate-200 border border-navy-border"
      }`}>
        <p className="whitespace-pre-wrap">{msg.text}</p>
      </div>
      {isUser && <User className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0" />}
    </div>
  );
}

class AddinErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[BonsaiOutlookPane] React error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding: 16, background: '#0d1424', color: 'white', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12}}>
          <p style={{fontSize: 14, color: 'var(--text-muted)', textAlign: 'center'}}>Something went wrong. Please restart.</p>
          <button
            onClick={() => { this.setState({hasError: false}); window.location.reload(); }}
            style={{background: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14}}
          >
            Restart
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function BonsaiOutlookPane() {
  const { loading, authenticated, configured, user_name, user_id, bonsaiApi, checkAuthStatus, auth_error } = useBonsai();
  const mateHeaders = () => ({ "X-Bonsai-User-Id": user_id || sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") });

  // ── Tab & UI state ──
  const [activeTab, setActiveTab] = useState("file");
  const [envInfo, setEnvInfo] = useState(null);
  const [officeReady, setOfficeReady] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginDebugMsg, setLoginDebugMsg] = useState("");
  const [showSettings, setShowSettings] = useState(false);

  // ── Shared filing state (managed here, passed to FileTab) ──
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [autoDeleteAfterFiling, setAutoDeleteAfterFiling] = useState(false);
  const [timeRecordingEnabled, setTimeRecordingEnabled] = useState(false);
  const [showTimePanel, setShowTimePanel] = useState(false);
  const [showBrowsePanel, setShowBrowsePanel] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [emailFilingStatus, setEmailFilingStatus] = useState(null); // {filed, matter_ref, needs_reply}
  const [batchFilingStatus, setBatchFilingStatus] = useState(null); // { folderName, total, batch_id, status }
  const batchPollRef = useRef(null);
  const browseFileHereRef = useRef(null);

  // Mail PR B — per-email attachment-save prompt for the browse-panel
  // batch drag-drop path.  We don't have per-email attachment counts at
  // drop-time, so the prompt headline counts emails (mode="bulk", with
  // `emailCount` driving the wording) and the decision is applied to the
  // whole batch.
  const [attachmentPromptOpen, setAttachmentPromptOpen] = useState(false);
  const [attachmentPromptCtx, setAttachmentPromptCtx] = useState(null);
  const [promptFolders, setPromptFolders] = useState([]);
  const [promptFoldersLoading, setPromptFoldersLoading] = useState(false);
  const [promptFoldersError, setPromptFoldersError] = useState(null);

  // Mail PR A FE — additional destinations picker state for the
  // browse-panel batch drag-drop path.  ONE picker modal applies the
  // same `additional_destinations` to every email in the batch (mirrors
  // PR #413's bulk attachment-save UX).
  const [destPickerOpen, setDestPickerOpen] = useState(false);
  const [destPickerCtx, setDestPickerCtx] = useState(null);
  const [destPickerSelection, setDestPickerSelection] = useState([]);
  const [destPickerFolders, setDestPickerFolders] = useState([]);
  const [destPickerFoldersLoading, setDestPickerFoldersLoading] = useState(false);
  const [destPickerFoldersError, setDestPickerFoldersError] = useState(null);

  // FIX(#182 Diff 5): close settings dropdown on outside click
  const settingsRef = useRef(null);
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);

  // ── Batch file from BrowsePanel drag-and-drop ──
  const handleBrowseBatchFile = useCallback(async ({ __error, emailIds, driveId, folderId, folderName, breadcrumb }) => {
    if (__error) {
      toast.error(_formatError(__error, 'Drop failed'));
      return;
    }
    let ids = emailIds;
    if (!ids || ids.length === 0) {
      ids = currentEmail?.id ? [currentEmail.id] : [];
    }
    if (ids.length === 0) {
      toast.error('No email selected to file');
      return;
    }

    const displayName = breadcrumb || folderName || 'folder';

    // Mail PR B — bulk attachment decision.  ONE prompt applies to all
    // emails in this drop.  Per-email prompts here would be hostile UX
    // (the user is dragging N emails onto a folder — they want a single
    // confirmation at most, not N).  We do not know the per-email
    // attachment counts at drop time, so use ids.length as a proxy
    // (`mode="bulk"` headline reads N emails / total attachments).
    const attachmentDecision = await new Promise((resolve) => {
      setPromptFolders([]);
      setPromptFoldersError(null);
      setAttachmentPromptCtx({
        attachmentCount: ids.length, // proxy — at least one possible per email
        emailCount: ids.length,
        mode: 'bulk',
        folderDriveId: driveId,
        folderItemId: folderId,
        resolve,
      });
      setAttachmentPromptOpen(true);
    });

    // Mail PR A FE — bulk additional destinations.  Open the picker
    // ONCE for the whole batch; selection applies to every email.
    // Zero selections is a valid "no copies" outcome.
    const additionalDestinations = await new Promise((resolve) => {
      setDestPickerSelection([]);
      setDestPickerFolders([]);
      setDestPickerFoldersError(null);
      setDestPickerCtx({
        emailCount: ids.length,
        folderDriveId: driveId,
        folderItemId: folderId,
        folderName,
        resolve,
      });
      setDestPickerOpen(true);
    });

    setBatchFilingStatus({ folderName: displayName, total: ids.length, status: 'starting' });
    toast.info(`Filing ${ids.length} email${ids.length !== 1 ? 's' : ''} to ${folderName}...`);

    try {
      const batchBody = {
        email_ids: ids,
        // PR #466 — explicit-gesture stamp. handleBrowseBatchFile is
        // wired to the BrowsePanel folder-row drop handler — the user
        // dragged email(s) onto a SharePoint folder row.
        user_intent: 'drag_drop',
        drive_id: driveId,
        folder_id: folderId,
        matter_reference: folderName,
        include_attachments: attachmentDecision.include_attachments,
        attachment_decision: attachmentDecision.attachment_decision,
        // Mail PR A FE — same additional copy destinations applied to
        // every email in the batch (PR #414 backend wiring).
        additional_destinations: additionalDestinations,
      };
      if (attachmentDecision.attachment_folder_id) {
        batchBody.attachment_folder_id = attachmentDecision.attachment_folder_id;
      }
      const resp = await bonsaiApi.post('/filing/batch', batchBody);
      const batchId = resp.data?.batch_id;
      if (batchId) {
        setBatchFilingStatus(prev => ({ ...prev, batch_id: batchId, status: 'processing' }));
        if (batchPollRef.current) clearInterval(batchPollRef.current);
        batchPollRef.current = setInterval(async () => {
          try {
            const statusResp = await bonsaiApi.get(`/filing/batch/${batchId}`);
            const batch = statusResp.data?.batch;
            if (!batch) return;
            setBatchFilingStatus(prev => ({
              ...prev,
              completed: batch.completed || 0,
              failed: batch.failed || 0,
              status: batch.status,
            }));
            if (batch.status === 'completed') {
              clearInterval(batchPollRef.current);
              batchPollRef.current = null;
              const failMsg = batch.failed > 0 ? ` (${batch.failed} failed)` : '';
              // Mail PR A FE — aggregate per-folder copy outcomes across
              // all emails in the batch.  The /filing/batch/{id} response
              // may surface additional_copies either at batch level or
              // per-result; handle both shapes.
              const allCopies = [];
              const flatten = (arr) => Array.isArray(arr) && arr.forEach((c) => allCopies.push(c));
              flatten(batch.additional_copies);
              if (Array.isArray(batch.results)) {
                batch.results.forEach((r) => flatten(r?.additional_copies));
              }
              const summary = summariseAdditionalCopies(allCopies);
              let copyMsg = '';
              if (summary.total > 0) {
                copyMsg = ` • ${summary.copied}/${summary.total} extra ${summary.total === 1 ? 'copy' : 'copies'}`;
                if (summary.inProgress > 0) copyMsg += `, ${summary.inProgress} still copying`;
                if (summary.failed > 0) copyMsg += `, ${summary.failed} failed`;
              }
              // PR #461 — surface the breadcrumb when it adds context
              // beyond the bare folder name (matter ref + path). Vanessa
              // needs to see WHERE the emails went, not just "Done".
              const destLabel = (breadcrumb && breadcrumb !== folderName) ? breadcrumb : folderName;
              const fullMsg = `${batch.completed} email${batch.completed !== 1 ? 's' : ''} filed to ${destLabel}${failMsg}${copyMsg}`;
              if (summary.anyFailed) {
                toast.error(fullMsg, { duration: 8000 });
              } else {
                toast.success(fullMsg);
              }
              setTimeout(() => setBatchFilingStatus(null), 5000);
              if (currentEmail?.id) checkEmailFilingStatus(currentEmail.id);
            }
          } catch {}
        }, 2000);
      }
    } catch (err) {
      setBatchFilingStatus(null);
      if (_isFolderNotFound(err)) {
        // Phantom folder — the SharePoint folder index in BrowsePanel is
        // stale. Offer a Refresh action that closes + reopens BrowsePanel
        // so its mount-time loadLocations() re-fetches from Graph.
        toast.error(
          `Destination folder no longer exists. It may have been moved or deleted.`,
          {
            duration: 10000,
            action: {
              label: 'Refresh folders',
              onClick: () => {
                setShowBrowsePanel(false);
                setTimeout(() => setShowBrowsePanel(true), 50);
              },
            },
          },
        );
        return;
      }
      toast.error(_formatError(err, 'Batch filing failed'));
    }
  }, [bonsaiApi, currentEmail]);

  useEffect(() => () => { if (batchPollRef.current) clearInterval(batchPollRef.current); }, []);

  // ── Mate state ──
  const [mateSuggestions, setMateSuggestions] = useState([]);
  const [mateSugLoading, setMateSugLoading] = useState(false);
  const [mateMessages, setMateMessages] = useState([]);
  const [mateInput, setMateInput] = useState("");
  const [mateSending, setMateSending] = useState(false);
  const [mateSessionId, setMateSessionId] = useState(null);
  const mateChatEndRef = useRef(null);
  const mateInputRef = useRef(null);

  // ── Auth: Login handler (dialog-based, no SSO) ──
  const handleOutlookLogin = async () => {
    setLoginLoading(true);
    setLoginDebugMsg("Connecting...");

    const dialogUrl = `https://bonsaimail.azurewebsites.net/api/bonsai/outlook/auth-start`;

    const canUseDialog = envInfo?.canUseDialog ||
      (typeof Office !== 'undefined' && typeof Office?.context?.ui?.displayDialogAsync === 'function');

    if (canUseDialog) {
      try {
        setLoginDebugMsg("Opening sign-in dialog...");
        Office.context.ui.displayDialogAsync(
          dialogUrl,
          { height: 60, width: 40, promptBeforeOpen: false },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const dialog = result.value;
              dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
                let userId = null;
                try {
                  const data = JSON.parse(arg.message);
                  userId = data.bonsai_user_id || null;
                } catch (e) {
                  try {
                    if (arg.message?.includes('bonsai_user_id')) {
                      userId = new URL(arg.message).searchParams.get('bonsai_user_id');
                    }
                  } catch {}
                }

                if (userId) {
                  try { localStorage.setItem('bonsai_user_id', userId); } catch {}
                  try { sessionStorage.setItem('bonsai_user_id', userId); } catch {}
                  try { document.cookie = `bonsai_user_id=${userId};path=/;max-age=2592000;SameSite=None;Secure`; } catch {}
                }

                try { dialog.close(); } catch {}
                setLoginLoading(false);

                if (userId) {
                  setLoginDebugMsg('Signed in! Loading...');
                  setTimeout(() => {
                    try { checkAuthStatus(); } catch {}
                  }, 800);
                }
              });
              dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
                setLoginDebugMsg(`Dialog closed. Code: ${arg?.error}`);
                setLoginLoading(false);
                if (arg?.error === 12006) setTimeout(() => checkAuthStatus(), 500);
              });
            } else {
              setLoginDebugMsg(`Dialog failed: ${result.error?.code}`);
              toast.error("Sign-in dialog failed. Please try again.");
              setLoginLoading(false);
            }
          }
        );
      } catch (e) {
        toast.error("Failed to open sign-in dialog.");
        setLoginLoading(false);
      }
    } else {
      window.location.href = dialogUrl;
    }
  };

  // ── Auth: Logout handler ──
  const handleLogout = useCallback(async () => {
    try {
      await bonsaiApi.post('/auth/logout');
    } catch {}
    sessionStorage.removeItem('bonsai_user_id');
    localStorage.removeItem('bonsai_user_id');
    setAutoDeleteAfterFiling(false);
    setTimeRecordingEnabled(false);
    setShowSettings(false);
    checkAuthStatus();
  }, [bonsaiApi, checkAuthStatus]);

  // ── Init: Office environment + email loading ──
  useEffect(() => {
    try {
      const initialEnv = detectOfficeEnvironment();
      setEnvInfo(initialEnv);
      if (typeof Office !== 'undefined' && Office !== null && typeof Office.onReady === 'function') {
        Office.onReady((info) => {
          const readyEnv = detectOfficeEnvironment();
          readyEnv.onReadyInfo = info;
          setEnvInfo(readyEnv);
          setOfficeReady(true);
          if (info.host === Office.HostType?.Outlook || info.host === "Outlook") {
            loadCurrentEmail();
          } else {
            setEmailLoading(false);
            setCurrentEmail({
              id: "demo-email-id", subject: "Demo Email - Testing Bonsai Mail",
              from: "sender@example.com", receivedDateTime: new Date().toISOString(),
              hasAttachments: true, internetMessageId: "<demo@example.com>"
            });
          }
        });
      } else {
        setOfficeReady(true);
        setEmailLoading(false);
        setCurrentEmail({
          id: "demo-email-id", subject: "Demo Email - Testing Bonsai Mail",
          from: "sender@example.com", receivedDateTime: new Date().toISOString(),
          hasAttachments: true, internetMessageId: "<demo@example.com>"
        });
      }
    } catch (initError) {
      setOfficeReady(true);
      setEmailLoading(false);
      setEnvInfo(prev => ({ ...prev, error: initError.message, details: [...(prev?.details || []), `Init error: ${initError.message}`] }));
    }
  }, []);

  // ── Load user preferences on auth ──
  useEffect(() => {
    if (!authenticated || !user_id) return;
    loadUserPreferences();
  }, [authenticated, user_id]);

  const loadUserPreferences = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/emails/preferences');
      if (resp.data) {
        setAutoDeleteAfterFiling(!!resp.data.auto_delete_after_filing);
        setTimeRecordingEnabled(!!resp.data.time_recording_enabled);
      }
    } catch {}
  }, [bonsaiApi]);

  const toggleAutoDelete = useCallback(async () => {
    const newVal = !autoDeleteAfterFiling;
    setAutoDeleteAfterFiling(newVal);
    try {
      await bonsaiApi.put('/emails/preferences', { auto_delete_after_filing: newVal });
    } catch {}
  }, [bonsaiApi, autoDeleteAfterFiling]);

  const toggleTimeRecording = useCallback(async () => {
    const newVal = !timeRecordingEnabled;
    setTimeRecordingEnabled(newVal);
    try {
      await bonsaiApi.put('/emails/preferences', { time_recording_enabled: newVal });
    } catch {}
  }, [bonsaiApi, timeRecordingEnabled]);

  // ── Load current email from Outlook ──
  const checkEmailFilingStatus = useCallback(async (emailId) => {
    if (!emailId || emailId === 'demo-email-id') return;
    try {
      const resp = await bonsaiApi.get('/filing/status/batch', { params: { email_ids: emailId } });
      const statuses = resp.data?.status || {};
      const filing = statuses[emailId];
      if (filing) {
        // filed_by_uncertain (14 May 2026) — the backend strips the
        // filed_by_name / filed_by_email stamp when the filing row
        // lacks an internet_message_id AND isn't the caller's own
        // filing. We surface the flag here so the pill can render
        // "(filer unknown)" instead of attributing to a stale stamp.
        // No silent default: filed_by stays null when the flag is
        // true so the pill render branches explicitly on it.
        const uncertain = filing.filed_by_uncertain === true;
        setEmailFilingStatus({
          filed: true,
          matter_ref: filing.matter_reference || null,
          folder_name: filing.folder_name || null,
          filed_by: uncertain ? null : (filing.filed_by_name || filing.filed_by_email || null),
          filed_by_uncertain: uncertain,
          filed_at: filing.filed_at || null,
          web_url: filing.sharepoint_url || filing.sharepoint_path || null,
          filing_id: filing.filing_id || null,
        });
      } else {
        setEmailFilingStatus({ filed: false });
      }
    } catch {
      setEmailFilingStatus(null);
    }
  }, [bonsaiApi]);

  const loadCurrentEmail = useCallback(() => {
    setEmailLoading(true);
    try {
      if (typeof Office === 'undefined' || !Office.context?.mailbox?.item) {
        setCurrentEmail(null);
        setEmailLoading(false);
        return;
      }
      const item = Office.context.mailbox.item;
      if (!item) { setCurrentEmail(null); setEmailLoading(false); return; }

      const emailData = {
        id: item.itemId,
        subject: item.subject,
        from: item.from?.emailAddress || item.sender?.emailAddress || "",
        fromName: item.from?.displayName || item.sender?.displayName || "",
        receivedDateTime: item.dateTimeCreated?.toISOString() || new Date().toISOString(),
        hasAttachments: item.attachments?.length > 0,
        internetMessageId: item.internetMessageId || "",
        conversationId: item.conversationId || "",
        body: "",
        parsedMatterRef: parseMatterRef(item.subject),
      };

      try {
        if (item.body && typeof item.body.getAsync === 'function') {
          item.body.getAsync("text", (result) => {
            if (result.status === "succeeded") {
              emailData.body = (result.value || "").substring(0, 2000);
            }
            setCurrentEmail(emailData);
            setEmailLoading(false);
          });
          return;
        }
      } catch {}

      setCurrentEmail(emailData);
    } catch (error) {
      console.error("[BonsaiOutlookPane] Error loading email:", error);
      setCurrentEmail(null);
    } finally {
      setEmailLoading(false);
    }
  }, []);

  // Check filing status when email changes
  useEffect(() => {
    if (currentEmail?.id && authenticated) {
      setEmailFilingStatus(null);
      checkEmailFilingStatus(currentEmail.id);
    }
  }, [currentEmail?.id, authenticated, checkEmailFilingStatus]);

  // ── Mate: auto-scroll ──
  useEffect(() => { mateChatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [mateMessages]);

  // ── Mate: load suggestions when tab activates ──
  useEffect(() => {
    if (activeTab === "mate" && authenticated) {
      fetchMateSuggestions();
      setTimeout(() => mateInputRef.current?.focus(), 200);
    }
  }, [activeTab, authenticated]);

  const fetchMateSuggestions = async () => {
    setMateSugLoading(true);
    try {
      const res = await axios.get(`${MATE_API}/mate/suggestions`, { headers: mateHeaders() });
      setMateSuggestions(res.data.suggestions || []);
    } catch { setMateSuggestions([]); }
    setMateSugLoading(false);
  };

  const emailContextString = useCallback(() => {
    if (!currentEmail) return "";
    const filing = emailFilingStatus?.filed
      ? `[FILED to SharePoint${emailFilingStatus.matter_ref ? ` — matter ${emailFilingStatus.matter_ref}` : ''}${emailFilingStatus.filed_by ? ` by ${emailFilingStatus.filed_by}` : ''}${emailFilingStatus.filed_at ? ` on ${new Date(emailFilingStatus.filed_at).toLocaleDateString('en-GB')}` : ''}]`
      : '[NOT YET FILED]';
    return `[Current email] Subject: "${currentEmail.subject || "(No Subject)"}" | From: ${currentEmail.fromName || currentEmail.from || "unknown"} | Date: ${currentEmail.receivedDateTime || ""} | Has attachments: ${currentEmail.hasAttachments ? "yes" : "no"} | ${filing}`;
  }, [currentEmail, emailFilingStatus]);

  const sendMateMessage = useCallback(async (text) => {
    if (!text?.trim()) return;
    setMateMessages(prev => [...prev, { role: "user", text: text.trim() }]);
    setMateInput("");
    setMateSending(true);
    try {
      const res = await axios.post(`${MATE_API}/mate/chat`, {
        message: text.trim(), session_id: mateSessionId, context: "outlook",
      }, { headers: mateHeaders() });
      setMateSessionId(res.data.session_id);
      setMateMessages(prev => [...prev, { role: "assistant", text: res.data.message }]);
    } catch (err) {
      const errMsg = errorMessage(err, "Unknown error");
      setMateMessages(prev => [...prev, { role: "assistant", text: `Error: ${errMsg}` }]);
    }
    setMateSending(false);
  }, [mateSessionId]);

  const clearMateChat = async () => {
    try { await axios.delete(`${MATE_API}/mate/history`, { headers: mateHeaders() }); } catch {}
    setMateMessages([]);
    setMateSessionId(null);
    toast.success("Chat cleared");
  };

  // ── Debug Banner (only shown when DEBUG_MODE = true) ──
  const DebugBanner = () => {
    if (!DEBUG_MODE) return null;
    return (
      <Card className="mb-4 border-purple-200 bg-purple-50">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <Bug className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs w-full">
              <p className="font-medium text-purple-800">Debug Info - v3.0</p>
              <div className="text-purple-700 mt-1 space-y-0.5">
                <p>Office: {envInfo?.hasOffice ? '✅ Yes' : '❌ No'}</p>
                <p>Context: {envInfo?.hasContext ? '✅ Yes' : '❌ No'}</p>
                <p>Host: {envInfo?.host || 'N/A'}</p>
                <p>Is Outlook: {envInfo?.isOutlook ? '✅ Yes' : '❌ No'}</p>
                <p>Has Mailbox: {envInfo?.hasMailbox ? '✅ Yes' : '❌ No'}</p>
                <p className="font-bold">Can Use Dialog: {envInfo?.canUseDialog ? '✅ YES' : '❌ NO - Will redirect'}</p>
                <p>Office Ready: {officeReady ? '✅ Yes' : '⏳ Waiting'}</p>
                {loginDebugMsg && <p className="mt-2 p-1 bg-purple-100 rounded font-mono text-[10px] break-all">Login: {loginDebugMsg}</p>}
                {envInfo?.details?.length > 0 && (
                  <details className="mt-1"><summary className="cursor-pointer">Details ({envInfo.details.length})</summary>
                    <ul className="ml-2 mt-1">{envInfo.details.map((d, i) => <li key={i}>• {d}</li>)}</ul>
                  </details>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── Loading state ──
  if (loading || emailLoading) {
    return (
      <div className="p-4 space-y-4" data-testid="outlook-pane-loading">
        <DebugBanner />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // ── Not configured (server cold start or unreachable) ──
  // BM-H130 -- surface the real error text and switch to a red "unreachable"
  // panel once the auto-retries have been exhausted, so Emily et al. can tell
  // the difference between "give it a moment" and "something is actually
  // broken, hit Retry or escalate".
  if (!configured) {
    const isFatal = auth_error && auth_error.startsWith("Cannot reach");
    const containerClass = isFatal
      ? "border-red-300 bg-red-50"
      : "border-yellow-200 bg-yellow-50";
    const iconClass = isFatal ? "text-red-600" : "text-yellow-600";
    const titleClass = isFatal ? "text-red-800" : "text-yellow-800";
    const bodyClass = isFatal ? "text-red-700" : "text-yellow-700";
    const linkClass = isFatal
      ? "text-red-800 hover:text-red-900"
      : "text-yellow-800 hover:text-yellow-900";
    const title = isFatal ? "Bonsai backend unreachable" : "Connecting to Backend...";
    const body = auth_error || "The server may be starting up.";
    return (
      <div className="p-4" data-testid="outlook-pane-not-configured" data-fatal={isFatal ? "true" : "false"}>
        <DebugBanner />
        <Card className={`border ${containerClass}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 ${iconClass} flex-shrink-0 mt-0.5`} />
              <div className="space-y-2">
                <p className={`text-sm font-medium ${titleClass}`}>{title}</p>
                <p className={`text-xs ${bodyClass}`} data-testid="outlook-pane-auth-error">{body}</p>
                <button onClick={checkAuthStatus} className={`text-xs font-medium underline ${linkClass}`} data-testid="retry-auth-btn">Retry now</button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Not authenticated ──
  if (!authenticated) {
    return (
      <div className="p-4" data-testid="outlook-pane-login">
        <DebugBanner />
        <Card className="border-navy-border">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-md bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-white mb-2">Sign in to Bonsai Mail</h3>
            <p className="text-sm text-slate-500 mb-4">Connect your Microsoft account to file emails to SharePoint</p>
            <Button onClick={handleOutlookLogin} disabled={loginLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="outlook-login-button">
              {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              {loginLoading ? "Signing in..." : "Sign in with Microsoft"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── No email selected ──
  if (!currentEmail) {
    return (
      <div className="p-4" data-testid="outlook-pane-no-email">
        <Card className="border-navy-border">
          <CardContent className="p-6 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-sm text-slate-500">Select an email in Outlook to file it</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={loadCurrentEmail}>
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main Task Pane UI ──
  //
  // BM-H P0-1 (01/09/2026): SPA preview banner. This React pane mounts ONLY
  // at /bonsai/outlook-preview in the SPA. The real Outlook add-in loads a
  // vanilla JS bundle (backend/bonsai/outlook/static/outlook-app.js, built
  // by build.sh) into #outlook-root inside outlook.html — this React tree
  // is never mounted in Outlook. Any bug fix made here does NOT ship to
  // the add-in unless it is also made in backend/bonsai/outlook/static/src/*.
  return (
    <div className="flex flex-col h-full relative" data-testid="outlook-pane-main">
      {/* SPA-preview banner — warns fee earners that this is not the add-in.
          Only renders when we are NOT running inside the Office task pane. */}
      {(typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) && (
        <div
          role="note"
          data-testid="outlook-preview-banner"
          style={{
            background: '#7c2d12',
            color: '#fff',
            padding: '6px 10px',
            fontSize: '11px',
            lineHeight: '1.35',
            borderBottom: '1px solid #431407',
          }}
        >
          <strong>SPA preview — not the live add-in.</strong> Fixes made in this
          React tree do not ship to Outlook. The real add-in is served from
          <code style={{ margin: '0 3px' }}>backend/bonsai/outlook/static/outlook.html</code>
          using the vanilla bundle built by <code>build.sh</code>.
        </div>
      )}
      {/* Header */}
      <div className="border-b border-navy-border bg-navy">
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
          <h2 className="text-sm font-semibold text-white">Bonsai Mail</h2>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{user_name}</span>
            <button onClick={() => setShowSettings(v => !v)} className="text-slate-400 hover:text-white p-0.5" data-testid="outlook-settings-btn">
              <Settings className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowBrowsePanel(true)} className="text-slate-400 hover:text-white p-0.5" data-testid="outlook-browse-btn" title="Browse SharePoint">
              <Globe className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowHelpPanel(true)} className="text-slate-400 hover:text-white p-0.5" data-testid="outlook-help-btn" title="Help & Tools">
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Settings dropdown — FIX(#182 Diff 5): ref attached for outside-click dismiss */}
        {showSettings && (
          <div ref={settingsRef} className="absolute right-2 top-10 z-40 w-56 rounded-lg border border-gray-700 bg-navy-card shadow-xl p-3 space-y-2" data-testid="settings-dropdown">
            <label className="flex items-center justify-between text-xs text-gray-300 cursor-pointer">
              Auto-delete after filing
              <input type="checkbox" checked={autoDeleteAfterFiling} onChange={toggleAutoDelete}
                className="rounded border-gray-600 bg-gray-800 text-teal focus:ring-teal w-3.5 h-3.5"
                data-testid="auto-delete-toggle" />
            </label>
            <label className="flex items-center justify-between text-xs text-gray-300 cursor-pointer">
              Time recording
              <input type="checkbox" checked={timeRecordingEnabled} onChange={toggleTimeRecording}
                className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500 w-3.5 h-3.5"
                data-testid="time-recording-toggle" />
            </label>
            <div className="border-t border-gray-700 pt-2">
              <button onClick={handleLogout} className="flex items-center gap-2 w-full text-xs text-status-red hover:text-status-red py-1" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5" />Sign out
              </button>
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex px-2 gap-1 pb-0" data-testid="outlook-tab-switcher">
          <button onClick={() => setActiveTab("file")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-t-md border-b-2 transition-colors ${activeTab === "file" ? "border-teal text-teal bg-navy-card" : "border-transparent text-slate-400 hover:text-slate-200"}`}
            data-testid="tab-file">
            <FolderOpen className="w-3.5 h-3.5" />File
          </button>
          <button onClick={() => setActiveTab("mate")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-t-md border-b-2 transition-colors ${activeTab === "mate" ? "border-teal text-teal bg-navy-card" : "border-transparent text-slate-400 hover:text-slate-200"}`}
            data-testid="tab-mate">
            <Sparkles className="w-3.5 h-3.5" />Bonsai Mate
          </button>
        </div>
      </div>

      {/* Email context bar + filing status */}
      <div className="px-3 py-2 border-b border-navy-border/50 bg-navy">
        <div className="flex items-start gap-2">
          <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{currentEmail.subject || "(No Subject)"}</p>
            <p className="text-[10px] text-slate-500 truncate">From: {currentEmail.fromName || currentEmail.from}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {currentEmail.hasAttachments && <Paperclip className="w-3.5 h-3.5 text-slate-500" />}
            {emailFilingStatus?.filed && (
              <div className="flex flex-col gap-0.5 items-end">
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium bg-teal-dim/40 text-teal border border-teal/40 rounded" data-testid="email-filed-badge">
                  <CheckCircle2 className="w-2.5 h-2.5 shrink-0" />
                  Filed{emailFilingStatus.matter_ref ? ` — ${emailFilingStatus.matter_ref}` : ''}
                </span>
                {(emailFilingStatus.filed_by || emailFilingStatus.filed_by_uncertain || emailFilingStatus.filed_at) && (
                  <span
                    className="text-[8px] text-slate-500 px-1"
                    data-testid="email-filed-by-sublabel"
                    data-filed-by-uncertain={emailFilingStatus.filed_by_uncertain ? 'true' : 'false'}
                  >
                    {/* Strict attribution (14 May 2026): when the backend
                        strips the filed_by stamp (filed_by_uncertain),
                        OR we simply have no name, render an explicit
                        "(filer unknown)" rather than letting the toast
                        drift into a confident "by <stale-name>". */}
                    {emailFilingStatus.filed_by
                      ? `by ${emailFilingStatus.filed_by}`
                      : '(filer unknown)'}
                    {emailFilingStatus.filed_at ? ` · ${new Date(emailFilingStatus.filed_at).toLocaleDateString('en-GB', {day:'numeric',month:'short'})}` : ''}
                  </span>
                )}
              </div>
            )}
            {currentEmail.parsedMatterRef && !emailFilingStatus?.filed && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium bg-blue-900/30 text-blue-400 border border-blue-800/40 rounded" data-testid="email-parsed-ref-badge">
                {currentEmail.parsedMatterRef}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ─── FILE TAB ─── */}
      {activeTab === "file" && (
        <FileTab
          userId={user_id}
          currentEmail={currentEmail}
          bonsaiApi={bonsaiApi}
          autoDeleteAfterFiling={autoDeleteAfterFiling}
          timeRecordingEnabled={timeRecordingEnabled}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          onOpenTimePanel={() => setShowTimePanel(true)}
          emailFilingStatus={emailFilingStatus}
          onFilingComplete={() => checkEmailFilingStatus(currentEmail?.id)}
          onRegisterFileHere={(fn) => { browseFileHereRef.current = fn; }}
        />
      )}

      {/* ─── BONSAI MATE TAB ─── */}
      {activeTab === "mate" && (
        <div className="flex-1 flex flex-col overflow-hidden" data-testid="outlook-mate-tab">
          <div className="flex-1 overflow-y-auto">
            {mateSuggestions.length > 0 && (
              <div className="px-3 py-2.5 border-b border-navy-border/50">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Zap className="w-3 h-3 text-teal" />
                  <span className="text-[9px] font-semibold text-teal uppercase tracking-wider">Attention needed</span>
                </div>
                <div className="space-y-1">
                  {mateSuggestions.slice(0, 4).map((s, i) => <MateSuggestionCard key={i} s={s} />)}
                </div>
              </div>
            )}
            {mateSugLoading && mateSuggestions.length === 0 && (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div>
            )}
            {mateMessages.length === 0 && (
              <div className="px-3 py-3">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Quick actions</p>
                <div className="space-y-1.5">
                  <button onClick={() => sendMateMessage("Triage my inbox — what needs my attention today?")}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-triage">
                    <Inbox className="w-3.5 h-3.5 text-status-amber shrink-0" /><span className="text-[11px] text-slate-300">Triage my inbox</span>
                  </button>
                  <button onClick={() => sendMateMessage(`Draft a reply to this email. ${emailContextString()}`)}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-draft-reply">
                    <Reply className="w-3.5 h-3.5 text-teal shrink-0" /><span className="text-[11px] text-slate-300">Draft reply</span>
                  </button>
                  <button onClick={() => { setActiveTab("file"); }}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-file-clear">
                    <FolderOpen className="w-3.5 h-3.5 text-status-green shrink-0" /><span className="text-[11px] text-slate-300">File & clear</span>
                  </button>
                  <button onClick={() => sendMateMessage(`Summarise this email concisely. ${emailContextString()}`)}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-summarise">
                    <FileText className="w-3.5 h-3.5 text-status-amber shrink-0" /><span className="text-[11px] text-slate-300">Summarise</span>
                  </button>
                  <button onClick={() => sendMateMessage(`Review the attached contract in this email and provide key points, obligations, risks and recommended actions. ${emailContextString()}`)}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-review-contract">
                    <FileCheck className="w-3.5 h-3.5 text-purple-400 shrink-0" /><span className="text-[11px] text-slate-300">Review contract</span>
                  </button>
                  <button onClick={() => sendMateMessage(`Draft an attendance note for this matter based on this email correspondence. ${emailContextString()}`)}
                    className="w-full text-left px-2.5 py-2 rounded border border-navy-border/60 bg-navy hover:border-teal/40 hover:bg-navy-hover transition-colors flex items-center gap-2"
                    data-testid="mate-quick-draft-file-note">
                    <ClipboardList className="w-3.5 h-3.5 text-blue-400 shrink-0" /><span className="text-[11px] text-slate-300">Draft file note</span>
                  </button>
                </div>
              </div>
            )}
            {mateMessages.length > 0 && (
              <div className="px-3 py-2.5 space-y-2.5">
                {mateMessages.map((msg, i) => <MateChatBubble key={i} msg={msg} />)}
                {mateSending && (
                  <div className="flex gap-1.5 items-center">
                    <Bot className="w-3.5 h-3.5 text-teal shrink-0" />
                    <div className="bg-navy border border-navy-border rounded-md px-2.5 py-1.5"><Loader2 className="w-3 h-3 animate-spin text-teal" /></div>
                  </div>
                )}
                <div ref={mateChatEndRef} />
              </div>
            )}
          </div>
          <div className="border-t border-navy-border px-3 py-2 bg-navy">
            <div className="flex items-center gap-1.5 mb-1.5">
              {mateMessages.length > 0 && (
                <button onClick={clearMateChat} className="text-slate-500 hover:text-slate-300 p-0.5" title="Clear chat" data-testid="mate-clear-chat"><Trash2 className="w-3 h-3" /></button>
              )}
              <button onClick={fetchMateSuggestions} className="text-slate-500 hover:text-slate-300 p-0.5" title="Refresh" data-testid="mate-refresh-suggestions"><RefreshCw className="w-3 h-3" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); sendMateMessage(mateInput); }} className="flex items-center gap-1.5">
              <input ref={mateInputRef} type="text" value={mateInput} onChange={(e) => setMateInput(e.target.value)}
                placeholder="Ask Mate about this email..."
                className="flex-1 bg-navy-card border border-navy-border rounded-md px-2.5 py-1.5 text-[11px] text-white placeholder:text-slate-500 focus:outline-none focus:border-teal/50"
                disabled={mateSending} data-testid="mate-outlook-chat-input" />
              <Button type="submit" size="sm" disabled={mateSending || !mateInput.trim()}
                className="bg-teal text-txt-faint hover:bg-teal-hover h-7 w-7 p-0" data-testid="mate-outlook-send-btn">
                <Send className="w-3 h-3" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* ─── TIME PANEL OVERLAY ─── */}
      {showTimePanel && (
        <TimePanel
          userId={user_id}
          currentEmail={currentEmail}
          bonsaiApi={bonsaiApi}
          selectedFolder={selectedFolder}
          onClose={() => setShowTimePanel(false)}
        />
      )}

      {/* Mail PR A FE — "+ Also file to..." control on the pane.
          Bulk drag-drop into BrowsePanel auto-prompts the picker as part
          of the batch flow; this button pre-arms a destination set for
          the next batch so the user can see/confirm the feature exists
          without having to drag first.  Selection is consumed by the
          next handleBrowseBatchFile call. */}
      {batchFilingStatus && (
        <button
          onClick={() => {
            // Re-open the picker for THIS batch by re-issuing the
            // resolver if one is pending; otherwise just open with the
            // current ctx so the user can adjust mid-flight.
            setDestPickerOpen(true);
          }}
          className="absolute bottom-12 right-2 z-40 text-[10px] text-teal hover:text-teal underline"
          data-testid="pane-also-file-to-button"
        >
          + Also file to...
        </button>
      )}

      {/* ─── BATCH FILING PROGRESS ─── */}
      {batchFilingStatus && (
        <div className="absolute bottom-2 left-2 right-2 z-40 p-2.5 rounded-lg border bg-gray-900/95 border-teal/50 shadow-lg" data-testid="batch-filing-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-teal truncate flex-1">
              {batchFilingStatus.status === 'completed'
                ? `Filed to ${batchFilingStatus.folderName}`
                : `Filing ${batchFilingStatus.total} email${batchFilingStatus.total !== 1 ? 's' : ''}...`}
            </span>
            <span className="text-[10px] text-gray-400 ml-2">
              {batchFilingStatus.status === 'completed'
                ? (batchFilingStatus.failed > 0 ? `${batchFilingStatus.completed} ok, ${batchFilingStatus.failed} failed` : 'Done')
                : `${batchFilingStatus.completed || 0}/${batchFilingStatus.total}`}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${
              batchFilingStatus.status === 'completed'
                ? (batchFilingStatus.failed > 0 ? 'bg-yellow-500' : 'bg-teal')
                : 'bg-teal animate-pulse'
            }`}
              style={{ width: `${batchFilingStatus.total ? Math.round(((batchFilingStatus.completed || 0) + (batchFilingStatus.failed || 0)) / batchFilingStatus.total * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* ─── BROWSE PANEL OVERLAY ─── */}
      {showBrowsePanel && (
        <BrowsePanel
          userId={user_id}
          bonsaiApi={bonsaiApi}
          onSelectFolder={(folder) => { setSelectedFolder(folder); setShowBrowsePanel(false); }}
          onClose={() => setShowBrowsePanel(false)}
          onBatchFile={handleBrowseBatchFile}
          onFileHere={(folder) => { browseFileHereRef.current?.(folder); setShowBrowsePanel(false); }}
        />
      )}

      {/* ─── HELP PANEL OVERLAY ─── */}
      {showHelpPanel && (
        <HelpPanel
          userId={user_id}
          bonsaiApi={bonsaiApi}
          onClose={() => setShowHelpPanel(false)}
        />
      )}

      {/* Mail PR A FE — bulk additional destinations picker.  Opens once
          per batch drag-drop after the attachment-save prompt; the
          selection applies to every email in the batch. */}
      <AdditionalDestinationsPicker
        isOpen={destPickerOpen}
        onClose={() => {
          setDestPickerOpen(false);
          // If the user dismisses without confirming, treat as zero
          // selections (no copies) — never silently apply a default.
          if (destPickerCtx?.resolve && !destPickerCtx.resolve.__handled) {
            destPickerCtx.resolve([]);
            destPickerCtx.resolve.__handled = true;
          }
          setDestPickerCtx(null);
        }}
        matterRef={destPickerCtx?.folderName || ''}
        currentSelection={destPickerSelection}
        emailCount={destPickerCtx?.emailCount || 1}
        folders={destPickerFolders}
        loadingFolders={destPickerFoldersLoading}
        foldersError={destPickerFoldersError}
        onLoadFolders={async () => {
          const driveId = destPickerCtx?.folderDriveId;
          const itemId = destPickerCtx?.folderItemId;
          if (!driveId || !itemId) {
            setDestPickerFoldersError(new Error('No matter folder configured.'));
            return;
          }
          setDestPickerFoldersLoading(true);
          setDestPickerFoldersError(null);
          try {
            const resp = await bonsaiApi.get(`/mail/matters/${driveId}/${itemId}/subfolders`);
            setDestPickerFolders(resp.data?.subfolders || []);
          } catch (e) {
            setDestPickerFoldersError(e);
            setDestPickerFolders([]);
          } finally {
            setDestPickerFoldersLoading(false);
          }
        }}
        onConfirm={(ids) => {
          if (destPickerCtx?.resolve) {
            destPickerCtx.resolve(ids);
            destPickerCtx.resolve.__handled = true;
          }
          setDestPickerOpen(false);
          setDestPickerCtx(null);
        }}
      />

      {/* Mail PR B — per-email attachment-save prompt for browse-panel
          batch drag-drop. */}
      <AttachmentSavePrompt
        open={attachmentPromptOpen}
        onOpenChange={(o) => {
          if (!o) {
            setAttachmentPromptOpen(false);
            if (attachmentPromptCtx?.resolve && !attachmentPromptCtx.resolve.__handled) {
              attachmentPromptCtx.resolve({
                include_attachments: false,
                attachment_folder_id: null,
                attachment_decision: 'skipped',
              });
            }
            setAttachmentPromptCtx(null);
          }
        }}
        attachmentCount={attachmentPromptCtx?.attachmentCount || 0}
        emailCount={attachmentPromptCtx?.emailCount || 1}
        mode={attachmentPromptCtx?.mode || 'bulk'}
        defaultFolderName="Documents"
        folders={promptFolders}
        loadingFolders={promptFoldersLoading}
        foldersError={promptFoldersError}
        onLoadFolders={async () => {
          const driveId = attachmentPromptCtx?.folderDriveId;
          const itemId = attachmentPromptCtx?.folderItemId;
          if (!driveId || !itemId) {
            setPromptFoldersError(new Error('No matter folder configured.'));
            return;
          }
          setPromptFoldersLoading(true);
          setPromptFoldersError(null);
          try {
            const resp = await bonsaiApi.get(`/mail/matters/${driveId}/${itemId}/subfolders`);
            setPromptFolders(resp.data?.subfolders || []);
          } catch (e) {
            setPromptFoldersError(e);
            setPromptFolders([]);
          } finally {
            setPromptFoldersLoading(false);
          }
        }}
        onDecision={(decision) => {
          if (attachmentPromptCtx?.resolve) {
            attachmentPromptCtx.resolve(decision);
            attachmentPromptCtx.resolve.__handled = true;
          }
          setAttachmentPromptOpen(false);
          setAttachmentPromptCtx(null);
        }}
      />
    </div>
  );
}

export default function BonsaiOutlookPaneWrapper() {
  return (
    <AddinErrorBoundary>
      <BonsaiOutlookPane />
    </AddinErrorBoundary>
  );
}
